/**
 * Delivery timeline dates (blocks/hadiyati-delivery-steps.liquid).
 *
 * Dates are computed in the browser from the day the page is viewed, in the Europe/Paris time
 * zone (the shop's), so a cached page never shows a stale date:
 * - step 1: today;
 * - step 2: today + preparation days (shown with « Au plus tard »);
 * - step 3: today + preparation + delivery days (shown with « Au plus tard »).
 * Month and year changes are handled by the calendar arithmetic of Date.UTC. The dates are
 * refreshed when the tab becomes visible again (e.g. left open overnight).
 */

const TIME_ZONE = 'Europe/Paris';

/** @returns {{ year: number, month: number, day: number }} Today in Paris */
function parisToday() {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date());
  const get = (/** @type {string} */ type) => Number(parts.find((part) => part.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/**
 * Short French date of today + offset days, e.g. "6 oct." ("1er nov." for the first day).
 * @param {number} offset
 */
export function formatDay(offset, today = parisToday()) {
  const date = new Date(Date.UTC(today.year, today.month - 1, today.day + offset));
  const month = new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC', month: 'short' }).format(date);
  const day = date.getUTCDate();
  return `${day === 1 ? '1er' : day} ${month}`;
}

class HadiyatiDeliverySteps extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.#render();
    document.addEventListener('hdy:express-state', () => this.#render(), { signal: this.#abort.signal });
    document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && this.#render(), {
      signal: this.#abort.signal,
    });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  #render() {
    // Express (order-wide service switched on, or already in the cart): shipping within 24 h; the
    // delivery time after shipping stays the same.
    const express = document.documentElement.hasAttribute('data-hdy-express');
    const delivery = Number(this.dataset.deliveryDays) || 0;
    const prep = express ? Number(this.dataset.expressDays) || 1 : Number(this.dataset.prepDays) || 0;
    const total = express ? prep + delivery : Number(this.dataset.totalDays) || prep;
    const today = parisToday();
    const dates = { 1: formatDay(0, today), 2: formatDay(prep, today), 3: formatDay(total, today) };

    for (const output of this.querySelectorAll('[data-hdy-step-date]')) {
      const step = /** @type {HTMLElement} */ (output).dataset.hdyStepDate;
      const text = dates[/** @type {1 | 2 | 3} */ (Number(step))];
      if (text && output.textContent !== text) output.textContent = text;
    }
    // « Au plus tard » only makes sense next to a date.
    for (const latest of this.querySelectorAll('[data-hdy-step-latest]')) {
      /** @type {HTMLElement} */ (latest).hidden = false;
    }
  }
}

if (!customElements.get('hadiyati-delivery-steps')) {
  customElements.define('hadiyati-delivery-steps', HadiyatiDeliverySteps);
}
